import { useQuery } from '@tanstack/react-query';
import type { MemberOpeningRoutingVenues } from '@pokerroom/types';
import CrmPointsWorkspace from './CrmPointsWorkspace';
import { CrmWorkflowProvider, useCrmWorkflow } from './CrmWorkflowContext';
import {
  beginCrmOperation,
  endCrmOperation,
  canLeaveCrm,
  readCrmRecovery,
} from './crmWorkspaceState';
import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { IconArrowLeft } from '@tabler/icons-react';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import { useCurrentVenue } from '../../hooks/useCurrentVenue';
import CrmImportPanel from './CrmImportPanel';
import { ALERT, DROPZONE, FIELD, NOTE, OUTLINE, PRIMARY } from './importStyles';

type Operation = { operationId: string; kind: 'FILE' | 'SHEETS' };
type ImportResult = {
  operationId: string;
  sourceKind: 'FILE' | 'SHEETS';
  state: string;
  total: number;
  processed: number;
  success: number;
  failed: number;
  pending: number;
  results: { row: number; memberNo: string; nickname: string; deleted?: boolean }[];
  errors: { row: number; error: string }[];
};
function restore(key: string): Operation | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) ?? 'null');
    return value &&
      typeof value.operationId === 'string' &&
      /^[0-9a-f-]{36}$/i.test(value.operationId) &&
      ['FILE', 'SHEETS'].includes(value.kind)
      ? value
      : null;
  } catch {
    return null;
  }
}
export default function BatchImportPage() {
  const scope = useAuthStore(
    (s) => `${s.user?.id}:${s.sessionRevision}:${s.allowedActions?.join(',')}`,
  );
  const { venue } = useCurrentVenue();
  return (
    <CrmWorkflowProvider key={`${scope}:${venue?.id}`} scope={`${scope}:${venue?.id}`}>
      <BatchImportContent />
    </CrmWorkflowProvider>
  );
}
function BatchImportContent() {
  const workflow = useCrmWorkflow();
  const { t } = useTranslation('member'),
    navigate = useNavigate();
  const session = useAuthStore((s) => s.sessionRevision);
  const { venue } = useCurrentVenue();
  const permitted = useAuthStore((s) => s.allowedActions?.includes('member:bulk_import') ?? false);
  const allowed = Boolean(venue) && permitted;
  const venueId = venue?.id ?? '',
    storageKey = 'member-import-operation:' + venueId;
  const [operation, setOperation] = useState<Operation | null>(() => restore(storageKey));
  const [mode, setMode] = useState<'FILE' | 'SHEETS' | 'CRM_MEMBERS' | 'CRM_POINTS'>(
    operation?.kind ?? 'FILE',
  );
  const actor = useAuthStore((s) => s.user?.id ?? '');
  const needsRecoveryContext = allowed && venue?.code === 'HUANBEI';
  const recoveryContext = useQuery({
    queryKey: ['opening-routing-venues', actor, session, venueId],
    queryFn: () =>
      api.get<MemberOpeningRoutingVenues>('/members/import/timed-opening/routing-venues'),
    enabled: needsRecoveryContext && !!actor,
    retry: false,
    staleTime: 30000,
  });
  const trustedRecovery =
    recoveryContext.data?.entryVenueId === venueId &&
    typeof recoveryContext.data.environment === 'string' &&
    !!recoveryContext.data.environment.trim();
  const recoveryBlocked =
    needsRecoveryContext &&
    (!trustedRecovery ||
      recoveryContext.isError ||
      readCrmRecovery(sessionStorage, actor, venueId, recoveryContext.data!.environment).length >
        0);
  // Check pending CRM work before any source can start; CRM points remains the recovery entry.
  if (workflow && mode !== 'CRM_POINTS') {
    workflow.guard.unresolved = recoveryBlocked;
    workflow.guard.newWritesAllowed = allowed && !recoveryBlocked;
  }
  const [file, setFile] = useState<File | null>(null),
    [sheets, setSheets] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null),
    [error, setError] = useState<string | null>(null),
    [running, setRunning] = useState(false);
  const stop = useRef(true),
    mounted = useRef(true),
    request = useRef<AbortController | null>(null),
    generation = useRef(0);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current++;
      stop.current = true;
      request.current?.abort();
    };
  }, []);
  useEffect(() => {
    generation.current++;
    stop.current = true;
    request.current?.abort();
    setRunning(false);
    setResult(null);
    setFile(null);
    setSheets('');
    const saved = restore(storageKey);
    setOperation(saved);
    setMode(saved?.kind ?? 'FILE');
  }, [storageKey, session]);
  useEffect(() => {
    if (!allowed) {
      generation.current++;
      stop.current = true;
      request.current?.abort();
      setRunning(false);
      return;
    }
    if (!operation || running) return;
    let active = true;
    api
      .get<ImportResult>('/members/import/operations/' + operation.operationId)
      .then((value) => {
        if (active) setResult(value);
      })
      .catch(() => {
        if (active) setError((previous) => previous ?? t('importResumeUnavailable'));
      });
    return () => {
      active = false;
    };
  }, [operation, allowed, session, running, t]);
  const onDrop = useCallback((files: File[]) => {
    if (files[0]) {
      setFile(files[0]);
      if (workflow) workflow.guard.dirty = true;
    }
  }, []);
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
    disabled: running,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
  });
  const start = async () => {
    if (
      running ||
      workflow?.guard.busy ||
      workflow?.guard.unresolved ||
      (workflow && !workflow.guard.newWritesAllowed) ||
      !allowed ||
      mode === 'CRM_MEMBERS' ||
      mode === 'CRM_POINTS'
    )
      return;
    const source =
      mode === 'FILE' ? file : (sheets.match(/\/d\/([a-zA-Z0-9_-]+)/)?.[1] ?? sheets.trim());
    if (!source) return;
    if (operation && operation.kind !== mode) {
      setError(t('importSourceChanged'));
      return;
    }
    const op = operation ?? { operationId: crypto.randomUUID(), kind: mode };
    const own = ++generation.current;
    setOperation(op);
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(op));
    } catch {
      setError(t('importResumeUnavailable'));
      return;
    }
    const deadline = performance.now() + 120000;
    let last = result?.processed ?? 0;
    const workflowToken = workflow ? beginCrmOperation(workflow.guard) : null;
    if (workflow && !workflowToken) return;
    stop.current = false;
    setRunning(true);
    setError(null);
    try {
      while (!stop.current && performance.now() < deadline) {
        const abort = new AbortController();
        request.current = abort;
        const config = {
          signal: abort.signal,
          timeout: Math.min(20000, Math.max(1, deadline - performance.now())),
        };
        let next: ImportResult;
        if (mode === 'FILE') {
          const form = new FormData();
          form.append('file', source as File);
          form.append('operationId', op.operationId);
          next = await api.post<ImportResult>('/members/import', form, {
            ...config,
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else
          next = await api.post<ImportResult>(
            '/members/import-google-sheets',
            { spreadsheetId: source, operationId: op.operationId },
            config,
          );
        if (!mounted.current || generation.current !== own) break;
        setResult(next);
        if (next.pending === 0) break;
        if (next.processed <= last) {
          setError(t('importNoProgress'));
          break;
        }
        last = next.processed;
      }
      if (mounted.current && generation.current === own && performance.now() >= deadline)
        setError(t('importRunPaused'));
    } catch {
      if (mounted.current && generation.current === own) setError(t('importRequestPaused'));
    } finally {
      if (workflow && workflowToken) endCrmOperation(workflow.guard, workflowToken);
      if (generation.current === own) {
        request.current = null;
        stop.current = true;
        if (mounted.current) setRunning(false);
      }
    }
  };
  const reset = () => {
    if (!window.confirm(t('importNewConfirm'))) return;
    generation.current++;
    stop.current = true;
    request.current?.abort();
    sessionStorage.removeItem(storageKey);
    setOperation(null);
    setResult(null);
    setFile(null);
    setSheets('');
    setError(null);
    setRunning(false);
    if (workflow) workflow.guard.dirty = false;
  };
  const complete = result?.pending === 0;
  const guard = workflow?.guard;
  useEffect(() => {
    if (complete && !running && (mode === 'FILE' || mode === 'SHEETS') && guard)
      guard.dirty = false;
  }, [complete, running, mode, guard]);
  const sources = ['FILE', 'SHEETS', 'CRM_MEMBERS', 'CRM_POINTS'] as const;
  const leave = () =>
    !running &&
    (!workflow || canLeaveCrm(workflow.guard, () => window.confirm(t('crmWorkspace.discard'))));
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (running || workflow?.guard.busy || workflow?.guard.dirty || workflow?.guard.unresolved) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [running, workflow]);
  return (
    <AdminPage>
      <AdminPageHeader
        title={t('importMembers')}
        back={
          <button
            type="button"
            className="relative inline-flex min-h-11 items-center gap-1 text-sm text-primary hover:underline"
            onClick={() => {
              if (leave()) navigate('/members');
            }}
          >
            <IconArrowLeft size={18} aria-hidden="true" />
            {t('backToList')}
          </button>
        }
      />
      <div className="admin-form-content">
        <div
          role="group"
          aria-label={t('importSource')}
          className="grid grid-cols-2 sm:flex mb-6 border-l border-t border-base-300 sm:border-l-0 sm:border-t-0"
        >
          {sources.map((kind) => (
            <button
              type="button"
              aria-pressed={mode === kind}
              disabled={
                running ||
                !!workflow?.guard.busy ||
                (!!workflow?.guard.unresolved &&
                  (kind !== 'CRM_POINTS' || mode === 'CRM_POINTS')) ||
                !allowed ||
                (kind === 'CRM_POINTS' && venue?.code !== 'HUANBEI')
              }
              key={kind}
              onClick={() => {
                if (kind === mode) return;
                const enteringRecovery =
                  kind === 'CRM_POINTS' &&
                  !workflow?.guard.busy &&
                  (!workflow?.guard.dirty || window.confirm(t('crmWorkspace.discard')));
                if (enteringRecovery || leave()) {
                  setMode(kind);
                  if (workflow) {
                    workflow.guard.dirty = false;
                    workflow.publish(null);
                  }
                }
              }}
              className={`min-h-11 px-4 text-sm border-r border-b border-base-300 sm:border sm:-ml-px sm:first:ml-0 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                mode === kind
                  ? 'bg-base-300 font-semibold text-base-content sm:border-base-content'
                  : 'text-base-content hover:bg-base-300'
              }`}
            >
              {kind === 'FILE'
                ? t('fileUpload')
                : kind === 'SHEETS'
                  ? 'Google Sheets'
                  : kind === 'CRM_POINTS'
                    ? t('crmWorkspace.title')
                    : t('crmWorkspace.members')}
            </button>
          ))}
        </div>
        {!allowed ? (
          <p role="alert" className={ALERT}>
            {t('crmWorkspace.denied')}
          </p>
        ) : mode === 'CRM_POINTS' ? (
          venue?.code === 'HUANBEI' ? (
            <CrmPointsWorkspace key={`${venueId}:${session}`} entryVenueId={venueId} />
          ) : (
            <p role="status">{t('crmWorkspace.unavailable')}</p>
          )
        ) : mode === 'CRM_MEMBERS' ? (
          <CrmImportPanel
            onDone={() => {
              if (leave()) navigate('/members');
            }}
          />
        ) : mode === 'FILE' || mode === 'SHEETS' ? (
          <div className="space-y-4">
            <p className="text-sm text-base-content">{t('importLimits')}</p>
            {mode === 'FILE' ? (
              <div
                {...getRootProps({ role: 'button' })}
                className={`${DROPZONE} ${isDragActive ? 'border-primary bg-primary/5' : 'border-base-300 hover:border-primary'}`}
              >
                <input {...getInputProps()} aria-label={t('fileUpload')} />
                <p className="font-medium text-base-content break-all">
                  {file?.name ?? t(isDragActive ? 'dropToUpload' : 'dragDropExcel')}
                </p>
                {!file && <p className="text-sm text-base-content/80">{t('clickToSelect')}</p>}
              </div>
            ) : (
              <div>
                <label htmlFor="import-sheets" className="block text-sm text-base-content mb-1">
                  {t('sheetsUrl')}
                </label>
                <input
                  id="import-sheets"
                  className={FIELD}
                  disabled={running}
                  value={sheets}
                  onChange={(e) => {
                    setSheets(e.target.value);
                    if (workflow) workflow.guard.dirty = !!e.target.value;
                  }}
                />
              </div>
            )}
            {fileRejections.length > 0 && (
              <p role="alert" className={ALERT}>
                {t('importLimits')}
              </p>
            )}
            {operation && <p className={NOTE}>{t('importResumeHint')}</p>}
            {result && (
              <div className="space-y-2 text-sm text-base-content">
                <p role="status" className="tabular-nums">
                  {t('importProgress', {
                    processed: result.processed,
                    total: result.total,
                    pending: result.pending,
                  })}
                </p>
                <p className="tabular-nums">
                  {t('importSuccessCount')}: {result.success}; {t('importFailedCount')}:{' '}
                  {result.failed}
                </p>
                {result.errors.length > 0 && (
                  <ul className="space-y-1 border border-error p-3">
                    {result.errors.map((e) => (
                      <li key={e.row}>
                        {t('rowNumber', { row: e.row })}: {e.error}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {error && (
              <p role="alert" className={ALERT}>
                {error}
              </p>
            )}
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                className={complete ? OUTLINE : PRIMARY}
                disabled={
                  !allowed ||
                  running ||
                  complete ||
                  !!workflow?.guard.unresolved ||
                  (mode === 'FILE' ? !file : !sheets.trim())
                }
                onClick={() => void start()}
              >
                {t('startImport')}
              </button>
              {running && (
                <button
                  type="button"
                  className={OUTLINE}
                  onClick={() => {
                    stop.current = true;
                    request.current?.abort();
                  }}
                >
                  {t('importPause')}
                </button>
              )}
              {operation && !running && (
                <button type="button" className={OUTLINE} onClick={reset}>
                  {t('importNewOperation')}
                </button>
              )}
              {complete && (
                <button
                  type="button"
                  className={PRIMARY}
                  onClick={() => {
                    if (leave()) navigate('/members');
                  }}
                >
                  {t('done')}
                </button>
              )}
            </div>
          </div>
        ) : (
          <p role="alert">{t('crmWorkspace.denied')}</p>
        )}
      </div>
    </AdminPage>
  );
}
